import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useController, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import RadioGroup from '@/components/atom/RadioGroup';
import {
  DEFAULT_VALUES,
  formFields,
  formSchema,
} from '@/src/pages/RecyclingProducts/upsertField';
import {
  getRecyclingProductsByIdAPI,
  putRecyclingProductAPI,
} from '@/src/apis/rest/recyclingProducts';
import { NAV_CONST } from '@/src/constants/nav';
import { UpsertRecyclingProductRequestBody } from '@/src/apis/rest/recyclingProducts.dtos';
import ImageUpload from '@/components/atom/ImageUpload';
import IconButton from '@/components/atom/Button/IconButton';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import FormTable from '@/src/pages/RecyclingProducts/components/FormTable';
import { optionFormColumn } from '@/src/pages/RecyclingProducts/components/optionFormColumn';
import { AgGridReact } from 'ag-grid-react';
import { Media } from '@/src/models/MeidaDto';
import { formatter } from '@/src/utils/formatter';
import { usePrompt } from '@/hooks/usePrompt';
import ImageUploadInfo from '@/components/molecule/ImageUploadInfo';
import PATH from '@/src/routes/path';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import SectionLabel from '@/components/molecule/SectionLabel';

const BACK_PATH = PATH.RECYCLING_PRODUCT_DETAIL;

function RecyclingProductEditPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();
  const [defaultData, setDefaultData] = useState('');
  const gridRef = useRef<AgGridReact>(null);
  const [deletedRowIds, setDeletedRowIds] = useState<number[]>([]);

  const form = useForm<
    UpsertRecyclingProductRequestBody & {
      productMedia: Media;
    }
  >({
    resolver: zodResolver(formSchema()),
    defaultValues: DEFAULT_VALUES,
  });

  const watchedFields = useWatch({
    control: form.control,
  });

  const { field: mediaField } = useController({
    name: 'productMedia',
    control: form.control,
  });

  const { data: productData } = useQuery({
    queryKey: ['recycling-product', id],
    queryFn: () => getRecyclingProductsByIdAPI(Number(id)),
    enabled: !!id,
    meta: {
      backUrl: BACK_PATH,
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
  });

  const { Dialog, isOpen, openDialog, closeDialog } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const putRecyclingProduct = useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: UpsertRecyclingProductRequestBody;
    }) => putRecyclingProductAPI(id, params),
  });

  const optionColDefs = useMemo(() => {
    return optionFormColumn({
      form,
      tableField: 'recyclingProductOptions',
    });
  }, [form]);

  const isFormValid = useMemo(() => {
    const options = watchedFields?.recyclingProductOptions;
    if (!form.formState.isValid || !options || options.length === 0)
      return false;

    for (const option of options) {
      if (
        !option.size?.toString() ||
        !option.weight?.toString() ||
        !option.carbonEmissionReduction?.toString() ||
        !option.pineScore?.toString()
      ) {
        return false;
      }
    }

    return true;
  }, [form, watchedFields]);

  const handleOnDeleteRow = useCallback((deletedId: number) => {
    setDeletedRowIds(prev => [...prev, deletedId]);
  }, []);

  // event
  const handleSubmit = useCallback(
    (values: UpsertRecyclingProductRequestBody) => {
      putRecyclingProduct.mutate(
        {
          id: formatter.STRING_TO_NUM(id!),
          params: { ...values, removeIds: deletedRowIds },
        },
        {
          onSuccess: () => {
            toast({
              variant: 'success',
              title: TOAST_CONST.saveSuccess.title,
              description: TOAST_CONST.saveSuccess.description,
            });

            navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
          },
          onError: e => {
            toast({
              variant: 'error',
              title: TOAST_CONST.error.title,
              description: e.message,
            });
          },
        },
      );
    },
    [id, navigate, putRecyclingProduct, toast, deletedRowIds],
  );

  useEffect(() => {
    if (productData) {
      form.reset({
        ...productData,
        productMediaId: productData.productMedia.id,
      });
      setDefaultData(
        JSON.stringify({
          ...productData,
          productMediaId: productData.productMedia.id,
        }),
      );
    }
  }, [productData, form]);

  usePrompt(
    defaultData !== JSON.stringify(watchedFields) &&
      !form.formState.isSubmitted,
  );

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => {
              navigate(-1);
            }}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={!isFormValid}
            type="button"
            onClick={openDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={putRecyclingProduct.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.recyclingProductManagement,
          NAV_CONST.recyclingProductEdit,
        ]}
        pageTitle={NAV_CONST.recyclingProductEdit}
      />
      <Form {...form}>
        <form
          ref={formRef}
          className="relative mb-[100px] flex flex-col gap-6 "
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <SectionCard cardTitle="수거 품목 정보">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6">
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name={formFields.category.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="수거 품목 명"
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder="품목 명을 입력해주세요"
                          onClear={() => field.onChange('')}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.isExposed.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="노출 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.exposed, value: 'true' },
                            { label: OPTIONS_CONST.notExposed, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name={formFields.isEventProduct.name}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent label="이벤트 여부" isRequired error={error}>
                        <RadioGroup
                          optionList={[
                            { label: OPTIONS_CONST.applied, value: 'true' },
                            { label: OPTIONS_CONST.notApplied, value: 'false' },
                          ]}
                          rootProps={{
                            value: field.value ? 'true' : 'false',
                            onValueChange: e => field.onChange(e === 'true'),
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
              <div className="border-r border-r-gray1" />
              <div className="flex flex-col gap-y-6">
                <FormField
                  control={form.control}
                  name="productMediaId"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        label="품목 이미지"
                        isRequired
                        error={error}
                        defaultMessage={
                          <ImageUploadInfo widthSize={335} heightSize={335} />
                        }
                      >
                        <ImageUpload
                          width={128}
                          height={128}
                          widthText={335}
                          heightText={335}
                          maxImageSizeMb={5}
                          imageClassName="object-cover"
                          value={mediaField?.value?.bufferFullUrl}
                          onChange={e => {
                            field.onChange(e.id);
                            mediaField.onChange(e);
                          }}
                          topAccessary={({ handleEdit, handleRemove }) => {
                            return (
                              <>
                                <div className="flex gap-x-1">
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'Pencil' }}
                                    onClick={() => {
                                      handleEdit();
                                    }}
                                  />
                                  <IconButton
                                    buttonSize="xSmall"
                                    buttonColor="outline"
                                    lucideIconProps={{ name: 'X' }}
                                    onClick={() => {
                                      handleRemove();
                                      field.onChange(undefined);
                                    }}
                                  />
                                </div>
                              </>
                            );
                          }}
                        />
                      </FormContent>
                    );
                  }}
                />
                <SectionLabel
                  labelProps={{ className: 'translate-y-2' }}
                  label="등록 일시"
                  value={
                    <Input
                      containerClassName="flex-1"
                      value={formatter.DATE(productData?.createdAt)}
                      disabled
                    />
                  }
                />
              </div>
            </div>
          </SectionCard>
          <SectionCard
            cardTitle="수거 품목 옵션 정보"
            contentProps={{ className: 'px-0 mt-4' }}
          >
            <FormTable
              form={form}
              ref={gridRef}
              tableField="recyclingProductOptions"
              rowData={productData?.recyclingProductOptions || []}
              columnDefs={optionColDefs}
              onDeleteRow={handleOnDeleteRow}
            />
          </SectionCard>
          {renderBottomFixedContent()}
        </form>
      </Form>
      {/* 모달 */}
      {isOpen && (
        <Dialog
          open={isOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openDialog();
            } else {
              closeDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putRecyclingProduct.isPending}
        />
      )}
    </>
  );
}

export default RecyclingProductEditPage;
